import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useSession } from "@/lib/session";
import type { Ticket, TicketMessage, TicketPriority, TicketStatus } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Head } from "@/ui/frame/Page";
import { Trail } from "@/ui/Trail";
import { Button } from "@/ui/Button";
import { Choice } from "@/ui/Choice";
import { Chip } from "@/ui/Chip";
import { TicketServices } from "@/features/support/TicketServices";
import { ErrorState, Skeleton } from "@/ui/States";
import { useToast } from "@/ui/Toast";
import { absoluteTime, relativeTime } from "@/lib/format";
import { Conversation } from "@/features/support/Conversation";
import {
  CATEGORY_LABEL,
  PRIORITIES,
  PRIORITY_LABEL,
  STAFF_STATUS_LABEL,
} from "@/features/support/tickets";
import { StaffStatus } from "./admin.tickets.index";

export const Route = createFileRoute("/_app/admin/tickets/$ticketId")({
  component: StaffTicketScreen,
});

interface TicketView {
  ticket: Ticket;
  messages: TicketMessage[];
}

const STATUSES: TicketStatus[] = [
  "open",
  "waiting_on_us",
  "waiting_on_customer",
  "resolved",
  "closed",
];

/**
 * One ticket, from the staff side.
 *
 * # WHAT IS HERE THAT IS NOT ON THE CUSTOMER'S SCREEN
 *
 * The private notes, the three controls that move a ticket, and a link into
 * the account it came from. The conversation itself is the same component:
 * the customer sees fewer messages, because the API filters the private ones
 * out in SQL, but they are the same messages drawn the same way — and two
 * renderings of one conversation is two places for "who said this" to be
 * drawn wrongly.
 *
 * Opening this page is recorded in the customer's own activity log, like
 * opening their organisation. Support can read everything a customer wrote to
 * us, and the customer can see that somebody did.
 */
function StaffTicketScreen() {
  const { ticketId } = Route.useParams();
  const { user } = useSession();
  const toast = useToast();
  const queryClient = useQueryClient();
  const key = ["admin-ticket", ticketId];

  const view = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => api.get<TicketView>(`/admin/tickets/${ticketId}`, undefined, signal),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: key });
    void queryClient.invalidateQueries({ queryKey: ["admin-tickets"] });
  };

  const reply = useMutation({
    mutationFn: (v: { body: string; internal: boolean }) =>
      api.post<{ ticket: Ticket }>(`/admin/tickets/${ticketId}/messages`, v),
    onSuccess: (_r, v) => {
      invalidate();
      if (v.internal) toast.success("Note saved", "Only staff can see it.");
    },
    onError: (e) => toast.error(e),
  });

  const update = useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      api.patch<{ ticket: Ticket }>(`/admin/tickets/${ticketId}`, patch),
    onSuccess: invalidate,
    onError: (e) => toast.error(e),
  });

  if (view.isError) {
    return (
      <div className="flex flex-col gap-5">
        <ErrorState error={view.error} onRetry={() => void view.refetch()} />
      </div>
    );
  }

  const t = view.data?.ticket;
  const messages = view.data?.messages ?? [];
  const mine = Boolean(t && user && t.assignee_name && t.assignee_name === user.display_name);

  return (
    <div className="flex flex-col gap-5">
      <Head
        above={
          <Trail
            trail={[
              { label: "Support", to: "/admin/tickets" },
              { label: t?.reference ?? "…" },
            ]}
          />
        }
        title={t?.subject ?? <Skeleton className="h-8 w-96" />}
        actions={
          t ? (
            <>
              {!mine && (
                <Button
                  tone="secondary"
                  busy={update.isPending}
                  onClick={() => update.mutate({ assignee: user?.id ?? "" })}
                >
                  Take it
                </Button>
              )}
              {t.status !== "closed" && (
                <Button
                  tone="primary"
                  busy={update.isPending}
                  onClick={() => update.mutate({ status: "resolved" })}
                >
                  Mark resolved
                </Button>
              )}
            </>
          ) : undefined
        }
      />

      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_19rem]">
        <div className="min-w-0">
          {view.isLoading ? (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : (
            <Conversation
              messages={messages}
              sending={reply.isPending}
              allowInternal
              placeholder="Answer the customer…"
              disabled={t?.status === "closed"}
              disabledReason="This ticket is closed. Reopen it above to add anything."
              onSend={(body, internal) => reply.mutate({ body, internal })}
            />
          )}
        </div>

        <div className="flex flex-col gap-4">
          <Panel title="This ticket">
            {!t ? (
              <Skeleton className="h-32 w-full" />
            ) : (
              <dl className="flex flex-col gap-3">
                <Row label="Reference">
                  <Chip>{t.reference}</Chip>
                </Row>
                <Row label="Account">
                  <Link
                    to="/admin/$orgId"
                    params={{ orgId: t.organisation_id }}
                    className="t-strong text-[var(--primary)] hover:underline"
                  >
                    {t.organisation_name}
                  </Link>
                </Row>
                <Row label="Opened by">
                  <span className="t-body">{t.opened_by_name}</span>
                </Row>
                <Row label="About">
                  <span className="t-body">{CATEGORY_LABEL[t.category]}</span>
                </Row>
                {t.services && t.services.length > 0 && (
                  <Row label="Services">
                    {/* The reason the field exists: triaging a technical
                        ticket without knowing which service it is about
                        starts with a reply asking. */}
                    <TicketServices services={t.services} orgId={t.organisation_id} staff />
                  </Row>
                )}
                <Row label="Opened">
                  <span className="t-body" title={absoluteTime(t.created_at)}>
                    {relativeTime(t.created_at)}
                  </span>
                </Row>
                <Row label="First answered">
                  {/* The one service figure worth keeping. Said in words
                      rather than shown as a blank, because a blank reads as
                      missing data and this is a fact: nobody has replied. */}
                  <span
                    className={
                      t.first_reply_at ? "t-body" : "t-body text-[var(--warning)]"
                    }
                    title={t.first_reply_at ? absoluteTime(t.first_reply_at) : undefined}
                  >
                    {t.first_reply_at ? relativeTime(t.first_reply_at) : "not yet"}
                  </span>
                </Row>
              </dl>
            )}
          </Panel>

          <Panel title="Move it">
            {!t ? (
              <Skeleton className="h-28 w-full" />
            ) : (
              <div className="flex flex-col gap-3">
                <label className="flex flex-col gap-1.5">
                  <span className="section-label">State</span>
                  <Choice
                    value={t.status}
                    onValueChange={(v) => update.mutate({ status: v })}
                    options={STATUSES.map((s) => ({ value: s, label: STAFF_STATUS_LABEL[s] }))}
                  />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="section-label">Priority</span>
                  <Choice
                    value={t.priority}
                    onValueChange={(v) => update.mutate({ priority: v })}
                    options={PRIORITIES.map((p: TicketPriority) => ({
                      value: p,
                      label: PRIORITY_LABEL[p],
                    }))}
                  />
                </label>
                <div className="flex flex-col gap-1.5">
                  <span className="section-label">Owner</span>
                  <div className="flex items-center gap-2">
                    <span className="t-body flex-1 truncate">{t.assignee_name || "Nobody"}</span>
                    {t.assignee_name ? (
                      <Button
                        tone="secondary"
                        size="dense"
                        onClick={() => update.mutate({ assignee: "" })}
                      >
                        Put it back
                      </Button>
                    ) : (
                      <Button
                        tone="secondary"
                        size="dense"
                        onClick={() => update.mutate({ assignee: user?.id ?? "" })}
                      >
                        Take it
                      </Button>
                    )}
                  </div>
                </div>
                <p className="t-meta border-t border-[var(--border)] pt-3">
                  Moving a ticket here is not announced. Only a reply is.
                </p>
              </div>
            )}
          </Panel>

          {t && (
            <div className="flex items-center justify-center">
              <StaffStatus status={t.status} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="section-label shrink-0">{label}</dt>
      <dd className="min-w-0 truncate text-right">{children}</dd>
    </div>
  );
}


